import { useState, useEffect } from 'react'
import { PencilIcon } from '@heroicons/react/24/solid'

import { UserProfile } from '../../store/slices/userSlice.types'

export function ProfileForm({
  profile,
  updateProfile,
}: {
  profile: UserProfile
  updateProfile: (updates: Partial<UserProfile>) => void
}) {
  const [name, setName] = useState(profile.name || '')
  const [signature, setSignature] = useState(profile.signature?.imageSrc || '')
  const [signatureName, setSignatureName] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const generateSignature = async (text: string) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''

    canvas.width = 400
    canvas.height = 100
    ctx.fillStyle = 'transparent'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Calculate font size based on text length
    const baseFontSize = 48
    const scaleFactor = text.length > 20 ? 20 / text.length : 1
    const fontSize = Math.max(30, Math.floor(baseFontSize * scaleFactor)) // Don't go smaller than 24px

    ctx.font = `${fontSize}px "Dancing Script"`
    ctx.fillStyle = 'black'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, canvas.width / 2, canvas.height / 2)

    return canvas.toDataURL('image/png')
  }

  useEffect(() => {
    const updateSignature = async () => {
      if (signatureName) {
        const newSignature = await generateSignature(signatureName)
        setSignature(newSignature)
      }
    }
    updateSignature()
  }, [signatureName])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsUpdating(true)
    await updateProfile({
      name,
      signature: { name: signatureName, imageSrc: signature },
    })
    setIsUpdating(false)
    setIsEditing(false)
  }

  if (!isEditing) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Personal Information
              </h3>
              <div className="flex flex-col">
                <div className="text-sm font-medium text-gray-500">Name:</div>
                <div className="mt-1 text-sm">
                  <p>{profile.name || 'No name set'}</p>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="text-sm font-medium text-gray-500">
                  Signature:
                </div>
                <div className="mt-1 text-sm">
                  {profile.signature ? (
                    <div className="flex flex-col">
                      <img
                        src={profile.signature.imageSrc}
                        alt="Your signature"
                        className="max-h-20 border border-gray-200 rounded p-2"
                      />
                      <p>{profile.signature.name}</p>
                    </div>
                  ) : (
                    <p>No signature set</p>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="mt-3 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:text-sm"
            >
              <PencilIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6 space-y-4">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Personal Information
        </h3>
        <div className="mt-5 sm:flex sm:items-center">
          <div className="w-full sm:max-w-xs">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              Name
            </label>
            <input
              type="text"
              name="name"
              id="name"
              className="mt-1 block w-full rounded-md py-2 px-3 border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-5">
          <label
            htmlFor="signatureName"
            className="block text-sm font-medium text-gray-700"
          >
            Signature
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="signatureName"
              id="signatureName"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              value={signatureName}
              maxLength={35}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Type your full name for signature"
            />
          </div>
          {signature && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700">
                Preview:
              </label>
              <img
                src={signature}
                alt="Signature preview"
                className="mt-1 max-h-20 border border-gray-200 rounded p-2"
              />
            </div>
          )}
        </div>
        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={isUpdating}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-6 py-2 font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setIsEditing(false)
              setName(profile.name || '')
              setSignature(profile.signature?.imageSrc || '')
              setSignatureName(profile.signature?.name || '')
            }}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  )
}
